/**
 * BuyerIndirectClaimsPanel — read-only strip inside /account/claims.
 *
 * Off-platform / past-PO commission claims that the Vinci Trade Desk
 * has authored on the buyer's behalf. Buyers can now acknowledge a
 * pending claim via a Security-Pulse-gated OTP so the desk sees
 * exactly when the buyer signed off.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, Clock, CalendarClock, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { SecurityPulseGate } from "./SecurityPulseGate";
import { useAuth } from "../context/AuthContext";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Slab-Sunset preview — mirrors backend services/slab_sunset.py.
const BOUNDARIES = [15, 30, 45];
const NEXT_PCT = { 15: 80, 30: 50, 45: 0 };

function nextSunset(daysGap) {
  const nb = BOUNDARIES.find((b) => daysGap < b);
  if (nb == null) return null;
  const daysLeft = nb - daysGap;
  return { boundary: nb, daysLeft, nextPct: NEXT_PCT[nb] ?? 0 };
}

function SunsetLine({ claim }) {
  const s = nextSunset(claim.days_gap);
  if (!s) {
    return (
      <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-2 font-body text-[11px] text-[#C0392B]" data-testid={`sunset-past-${claim.id}`}>
        <CalendarClock size={12} />
        Past every slab boundary · Board escalation only
      </div>
    );
  }
  const urgent = s.daysLeft <= 3;
  const c = urgent ? "#C0392B" : s.daysLeft <= 7 ? "#F5D67A" : "#8A8A82";
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-2 font-body text-[11px]"
         style={{ color: c }} data-testid={`sunset-line-${claim.id}`}>
      <CalendarClock size={12} />
      <span>
        Slab sunset in <b>{s.daysLeft} day{s.daysLeft === 1 ? "" : "s"}</b> —
        commission drops from {claim.slab_payout_pct}% to <b>{s.nextPct}%</b>
        {urgent && <span className="ml-2 uppercase tracking-[0.18em]">· T-3d alert</span>}
      </span>
    </div>
  );
}

function SlabPill({ pct }) {
  const c = pct >= 100 ? "#2FB999"
          : pct >= 80  ? "#C9A961"
          : pct >= 50  ? "#F5D67A"
          : "#C0392B";
  return <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.25em]"
    style={{ color: c, borderColor: `${c}55` }}>{pct}% slab</span>;
}

function StatusPill({ status }) {
  const map = {
    pending:  ["#F5D67A", "Pending Trade Desk", Clock],
    approved: ["#2FB999", "Approved", ShieldCheck],
  };
  const [c, l, Icon] = map[status] || ["#8A8A82", status, Clock];
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.25em]"
      style={{ color: c, borderColor: `${c}55` }}>
      <Icon size={10} />{l}
    </span>
  );
}

export function BuyerIndirectClaimsPanel({ api }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulseFor, setPulseFor] = useState(null); // claim id being acknowledged

  const load = async () => {
    try { setData((await api.get("/claims/my-indirect")).data); }
    catch { /* silent — the panel just doesn't render */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [api]);

  const doAck = async (otp_code) => {
    const cid = pulseFor;
    setPulseFor(null);
    try {
      await api.post(`/claims/my-indirect/${cid}/acknowledge`, { otp_code });
      toast.success("Commission claim acknowledged — the trade desk has been notified.");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.code || "Could not acknowledge — please retry.");
    }
  };

  if (loading) return null;
  if (!data || (data.claims || []).length === 0) return null;

  return (
    <div className="mt-10 rounded-sm border border-[#8B1E1E]/40 bg-gradient-to-br from-[#1a0d0a] via-[#0f0908] to-[#0a0606] p-6" data-testid="buyer-indirect-panel">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle size={22} className="text-[#C0392B] mt-0.5 shrink-0" />
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C0392B]">Off-platform commissions · Trade Desk authored</div>
          <h3 className="font-serif text-xl text-[#F5F5F0] mt-1">Indirect (past-PO) commissions on record</h3>
          <p className="font-body text-[12px] text-[#C9C9C2]/85 mt-1 leading-relaxed max-w-3xl">
            {data.notice}
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {data.claims.map((c) => (
          <div key={c.id} data-testid={`buyer-indirect-row-${c.id}`}
            className="rounded-sm border border-white/10 bg-black/40 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-serif text-[#F5F5F0] truncate">#{c.id.slice(0, 8).toUpperCase()} · {c.order_ref || "—"}</div>
                <div className="font-body text-[11px] text-[#8A8A82] mt-1">
                  Fulfilled {c.fulfillment_date} · Applied {c.applying_date} · gap <b>{c.days_gap}d</b>
                  {c.material ? ` · ${c.material}` : ""}
                  {c.quantity ? ` · ${c.quantity}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <SlabPill pct={c.slab_payout_pct} />
                <StatusPill status={c.trade_desk_approval_status} />
                {c.buyer_acknowledged && (
                  <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#5EE6D0] border-[#5EE6D0]/55"
                    data-testid={`ack-badge-${c.id}`}>
                    <BadgeCheck size={10} /> Acknowledged
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3 grid md:grid-cols-3 gap-3 font-body text-[12px]">
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Order value</div>
                <div className="text-[#F5F5F0] tabular-nums mt-0.5">{inr(c.order_value)}</div>
              </div>
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Eligible commission</div>
                <div className="text-[#C9C9C2] tabular-nums mt-0.5">
                  {inr(c.eligible_commission_inr)}{" "}
                  <span className="text-[#8A8A82]">@ {c.base_rate_pct}%</span>
                </div>
              </div>
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A8A82]">Payable to you</div>
                <div className="font-serif text-lg tabular-nums mt-0.5"
                  style={{ color: c.trade_desk_approval_status === "approved" ? "#2FB999" : "#F5F5F0" }}>
                  {inr(c.amount_claimed)}
                </div>
                {c.deduction_inr > 0 && (
                  <div className="font-body text-[10px] text-[#C0392B] tabular-nums mt-0.5">
                    − {inr(c.deduction_inr)} slab reduction
                  </div>
                )}
              </div>
            </div>
            {/* Slab-sunset preview — mirrors T-3d cron alert */}
            <SunsetLine claim={c} />
            {c.trade_desk_approval_status === "approved" && c.trade_desk_decided_at && (
              <div className="mt-3 pt-3 border-t border-white/[0.06] font-body text-[11px] text-[#8A8A82]">
                Approved by {c.trade_desk_decided_by} at {c.trade_desk_decided_at.slice(0, 19)} UTC
                {c.trade_desk_decision_reason ? ` · ${c.trade_desk_decision_reason}` : ""}
              </div>
            )}
            {!c.buyer_acknowledged && c.trade_desk_approval_status !== "rejected" && (
              <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-end">
                <button
                  type="button"
                  data-testid={`ack-btn-${c.id}`}
                  onClick={() => setPulseFor(c.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-[#5EE6D0]/45 text-[#5EE6D0] px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#5EE6D0]/10 transition-colors"
                >
                  <ShieldCheck size={12} /> Acknowledge with Security Pulse
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <SecurityPulseGate
        open={!!pulseFor}
        email={user?.email || ""}
        purpose="commission_claim"
        displayName={user?.name}
        onClose={() => setPulseFor(null)}
        onVerified={doAck}
      />
    </div>
  );
}
